import type { ImportCounts, ImportPageState, ImportPreviewOrg } from "../../utils/bpaImport";
import { firmKey } from "../../utils/bpaImport";
import { SPLIT_OPEN_QUERY_KEY } from "../../utils/bpaMerge";

/** Carried in router state from the import page to the target BPA page for the done banner. */
export type ImportDoneState = {
  sourceEditionBpaId: string;
  sourceBpaName: string;
  organisations: number;
  imported: ImportCounts;
  withScores: boolean;
  importedCompanyIds: string[];
};

export const IMPORT_DONE_KEY = "importDone";

function isCounts(value: unknown): value is ImportCounts {
  if (typeof value !== "object" || value === null) return false;
  const counts = value as Record<string, unknown>;
  return (
    typeof counts.submissions === "number" &&
    typeof counts.rankings === "number" &&
    typeof counts.commentary === "number"
  );
}

function readCompanyIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === "string");
}

export function readImportDone(locationState: unknown): ImportDoneState | null {
  if (typeof locationState !== "object" || locationState === null) return null;
  const done = (locationState as Record<string, unknown>)[IMPORT_DONE_KEY];
  if (typeof done !== "object" || done === null) return null;
  const candidate = done as Record<string, unknown>;
  if (
    typeof candidate.sourceEditionBpaId !== "string" ||
    typeof candidate.sourceBpaName !== "string" ||
    typeof candidate.organisations !== "number" ||
    typeof candidate.withScores !== "boolean" ||
    !isCounts(candidate.imported)
  ) {
    return null;
  }
  return {
    ...(candidate as ImportDoneState),
    importedCompanyIds: readCompanyIds(candidate.importedCompanyIds),
  };
}

export const IMPORT_INVALIDATED_QUERY_KEYS = [
  "research-org-list",
  "research-ranking-tables",
  "import-history",
  "bpa-import-history",
  "research-bpa-stats",
  "research-bpa-detail",
  "research-edition-bpas",
  "split-hub-still-in",
  "split-hub-overview",
  SPLIT_OPEN_QUERY_KEY,
];

export function broughtCompanyIds(orgs: ImportPreviewOrg[], state: ImportPageState): string[] {
  return orgs
    .filter(org => org.companyId !== null && !state.unticked.includes(firmKey(org)))
    .filter(org => state.kinds.some(kind => org.importCounts[kind] > 0))
    .map(org => org.companyId as string);
}
